import React, { useContext } from 'react';
import { Alert, Box, Button, FormField, Input, Select, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { ColumnDataType, PeriodBehavior } from 'src/models/segment-cdt/SegmentCdtTypes';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { buildColumnDef, PERIOD_BEHAVIOR_OPTIONS } from 'src/components/segment-cdt/create/segmentCreateUtils';

const DATA_TYPE_OPTIONS = [
    { value: ColumnDataType.NUMBER, label: 'Number' },
    { value: ColumnDataType.PERCENTAGE, label: 'Percentage' },
    { value: ColumnDataType.TEXT, label: 'Text' },
];

/**
 * "Manual Upload" column config: a blank column filled later via Upload. Captures displayName +
 * dataType, and for a NUMBER column an optional Default Value (the Common Balance Pool join default).
 *
 * <p><b>No currency is collected.</b> It is not in the mocks, and what a currency would mean on a
 * hand-uploaded column is unsettled: there is no FX to apply, so a rate of 1 is the only sensible reading and
 * the uploader is expected to enter values already in the currency they intend to use. Left out until product
 * settles it rather than asking for a value nobody can act on. `buildColumnDef` sends `STAT` for the stored
 * column, which is the statistical / no-conversion marker and what the backend requires of a NUMBER column.
 */
export default function ManualColumnForm({ hideCancel = false }: { hideCancel?: boolean }) {
    const { state, dispatch } = useContext(SegmentTablesContext);
    const cw = state.createWizard;
    const editor = cw.columnEditor;
    const draft = editor?.draft ?? {};
    // The refusal for this draft, held beside it in the reducer so SET_COLUMN_DRAFT drops it on any edit.
    const localError = editor?.error ?? '';
    const setDraft = (patch: any) => dispatch(ACTIONS.SET_COLUMN_DRAFT.withPayload(patch));

    const onAdd = () => {
        // Duplicate checks span the union of the table's existing columns (empty in the create wizard,
        // populated when adding columns to an existing table) and the queued columns.
        const union = [...(state.detail.definition?.columns ?? []), ...cw.columns];
        const others = union.map((c) => c.displayName || '');
        const otherIds = union.map((c) => c.columnId).filter((id): id is string => !!id);
        // No currency list is needed: this form collects no currency, so there is nothing to check it against.
        const { column, error } = buildColumnDef(draft, others, otherIds, [], union);
        if (error || !column) {
            dispatch(ACTIONS.SET_COLUMN_ERROR.withPayload(error || 'Invalid column.'));
            return;
        }
        dispatch(ACTIONS.COMMIT_COLUMN.withPayload(column));
    };

    const isNumber = draft.dataType === ColumnDataType.NUMBER;

    return (
        <SpaceBetween size="m">
            <FormField label="Column name" description="Enter a unique name for this column.">
                <Input
                    value={draft.displayName ?? ''}
                    placeholder="e.g. Adjustments"
                    onChange={({ detail }) => setDraft({ displayName: detail.value })}
                />
            </FormField>
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <img src={CONSTANTS.IMAGE_PATH + 'segment-manual-upload.svg'} width={28} height={28} alt="" style={{ display: 'block' }} />
                <Box variant="h4" padding={{ top: 'n', bottom: 'n' }}>Configure column: Manual Upload</Box>
            </SpaceBetween>
            <FormField label="Data type">
                <Select
                    selectedOption={DATA_TYPE_OPTIONS.find((o) => o.value === draft.dataType) ?? null}
                    options={DATA_TYPE_OPTIONS}
                    placeholder="Select a data type"
                    onChange={({ detail }) => setDraft({ dataType: detail.selectedOption.value as ColumnDataType })}
                />
            </FormField>
            {isNumber && (
                <FormField
                    label="Default value (CBP join)"
                    description="Optional. A number used for a segment with no row here when this table is joined in a Common Balance Pool."
                >
                    {/* A plain text input, not type="number": a native number field reports an empty value
                        for a mid-edit state like "-" or "1.", which would blank the character as the user
                        types it. The raw string is kept as-is (see the draft field) and validated at build
                        time by checkDefaultValue, which mirrors the backend's decimal parse. */}
                    <Input
                        value={draft.defaultValue ?? ''}
                        placeholder="e.g. 0"
                        onChange={({ detail }) => setDraft({ defaultValue: detail.value })}
                    />
                </FormField>
            )}
            <FormField
                label="Period behaviour"
                description="What happens to entered values when a new period opens."
            >
                <Select
                    selectedOption={PERIOD_BEHAVIOR_OPTIONS.find((o) => o.value === draft.periodBehavior) ?? null}
                    // "Not set" is offered only once a behaviour has been chosen, so it reads as clearing a
                    // choice rather than as a third behaviour to pick from.
                    options={draft.periodBehavior
                        ? [{ value: '', label: 'Not set' }, ...PERIOD_BEHAVIOR_OPTIONS]
                        : PERIOD_BEHAVIOR_OPTIONS}
                    placeholder="Optional - not set"
                    onChange={({ detail }) => setDraft({
                        periodBehavior: (detail.selectedOption.value || undefined) as PeriodBehavior | undefined,
                    })}
                />
            </FormField>
            <Alert type="info">The column is added blank. Fill its values later via Upload. There is no inline editing here.</Alert>
            {localError && <Alert type="error">{localError}</Alert>}
            <Box float="right">
                <SpaceBetween size="xs" direction="horizontal">
                    {!hideCancel && <Button onClick={() => dispatch(ACTIONS.CANCEL_COLUMN_EDITOR)}>Cancel</Button>}
                    <Button variant="primary" onClick={onAdd}>
                        Add empty column
                    </Button>
                </SpaceBetween>
            </Box>
        </SpaceBetween>
    );
}
